import api from './index';
import { getUserId } from './auth';

export const applicationService = {
  // Get applications for a specific user
  getApplicationsByUser: async (userId) => {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const response = await api.get('/applications', {
        params: { userId }
      });

      return response.data;
    } catch (error) {
      console.error(`Error fetching applications for user ${userId}:`, error);
      throw error;
    }
  },

  // Get all applications (admin only)
  getAllApplications: async () => {
    try {
      const response = await api.get('/applications?admin=true');
      
      // Ensure we return an array, handle different response formats
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray(data.applications)) {
        return data.applications;
      } else {
        console.warn('Unexpected response format from getAllApplications:', data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching all applications:', error);
      // Return empty array instead of throwing to prevent frontend crashes
      return [];
    }
  },

  // Get applications for a trademark
  getApplications: async (trademarkId) => {
    try {
      const response = await api.get('/applications', {
        params: { trademarkId }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching applications for trademark ${trademarkId}:`, error);
      throw error;
    }
  },
  
  // Get a specific application
  getApplication: async (applicationId) => {
    try {
      const response = await api.get('/applications', {
        params: { id: applicationId }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching application ${applicationId}:`, error);
      throw error;
    }
  },
  
  // Create a new application (supports legacy and dashboard payloads)
  createApplication: async (...args) => {
    try {
      if (args.length === 1 && typeof args[0] === 'object' && !Array.isArray(args[0])) {
        const payload = args[0];
        if (!payload.userId) {
          payload.userId = getUserId();
        }
        if (!payload.userId) {
          throw new Error('User not authenticated');
        }
        const response = await api.post('/applications', payload);
        return response.data;
      }

      const [trademarkId, country, classNumbers] = args;
      const response = await api.post('/applications', {
        trademarkId,
        country,
        filingDetails: {
          classes: classNumbers
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating application:', error);
      throw error;
    }
  },
  
  // Get country-specific requirements
  getCountryRequirements: async (country) => {
    try {
      const response = await api.get('/applications', {
        params: { countryRequirements: country }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching requirements for ${country}:`, error);
      throw error;
    }
  },
  
  // Country specialist methods for detailed country-specific info
  countrySpecialist: {
    // Get country-specific requirements for trademark filing
    getRequirements: async (countryCode, trademark, sessionId) => {
      try {
        const response = await api.get('/countries/requirements', {
          params: {
            countryCode,
            trademarkId: trademark?.id,
            sessionId
          }
        });
        return response.data;
      } catch (error) {
        console.error(`Error fetching requirements for ${countryCode}:`, error);
        throw error;
      }
    }
  },
  
  // Update trademark application with selected countries, classes, etc.
  updateTrademarkApplication: async (trademarkId, countries, classes, descriptions, totalPrice, userId = null) => {
    try {
      // Get authenticated user ID
      const authUserId = userId || getUserId();
      
      if (!authUserId) {
        throw new Error('User not authenticated');
      }
      
      const response = await api.post('/applications', {
        trademarkId,
        countries,
        classes,
        descriptions,
        totalPrice,
        userId: authUserId,
        timestamp: new Date().toISOString()
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating trademark application ${trademarkId}:`, error);
      throw error;
    }
  },
  
  // Update application status
  updateApplicationStatus: async (applicationId, status, details = {}) => {
    try {
      const response = await api.post('/applications', {
        id: applicationId,
        status,
        ...details
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating application ${applicationId} status:`, error);
      throw error;
    }
  },
  
  // Update trademark filing details (country and class selections)
  updateFilingDetails: async (trademarkId, countries, classes, goodsServices) => {
    try {
      const response = await api.post('/Database/trademarks/update', {
        id: trademarkId,
        filing: {
          selectedCountries: countries,
          selectedClasses: classes,
          goodsServices
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating filing details for trademark ${trademarkId}:`, error);
      throw error;
    }
  },
  
  // Cancel an application
  cancelApplication: async (applicationId) => {
    try {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }
      
      const response = await api.post('/applications', {
        id: applicationId,
        status: 'cancelled'
      });
      return response.data;
    } catch (error) {
      console.error(`Error cancelling application ${applicationId}:`, error);
      throw error;
    }
  },

  // Get filing statuses for a trademark across multiple countries
  getFilingStatuses: async (trademarkId, countries, userId = null) => {
    try {
      // Get authenticated user ID
      const authUserId = userId || getUserId();
      
      if (!authUserId) {
        throw new Error('User not authenticated');
      }
      
      const response = await api.get('/applications/status', {
        params: {
          trademarkId,
          countries: countries.join(','),
          userId: authUserId
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching filing statuses for trademark ${trademarkId}:`, error);
      throw error;
    }
  }
};

export default applicationService;
